import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  Radar,
  Search,
  TriangleAlert,
  Undo2,
} from 'lucide-react'
import { apiGet, apiPut } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'
import {
  Derived,
  Field,
  Note,
  SectionTabs,
  SettingCard,
  displayValue,
} from '@/components/settings/controls'
import {
  BurstCostLine,
  ClaimCostLine,
  HuntNowList,
  IspBudgetLine,
  NoUuidRateLine,
  PoolBudgetLine,
  ReleaseIspTool,
  ReleasePoolTool,
  RenewHeadroomNote,
  WatchRateLine,
} from '@/components/settings/panels'
import { SoundsPanel } from '@/components/settings/sounds'
import {
  FIELD_DEFS,
  HUNT_SETTINGS_KEY,
  TABS,
  changedKeys,
  clockLabel,
  draftFrom,
  marketHost,
  marketLabel,
  same,
  searchFields,
  toPayload,
  type Draft,
  type FieldKey,
  type HuntSettingsDto,
  type TabId,
} from '@/components/settings/fields'

/**
 * Hunt settings — how hard we race for a shift.
 *
 * ── What this file is, and what it deliberately is not ──────────────
 * It is the STRUCTURE of the page: which cards exist, which of them are
 * conditional, and which computed lines sit between them. It is not where a
 * setting's text, unit or allowed range lives — every one of those is described
 * once in `components/settings/fields.ts`, which is what makes the search box,
 * the per-section change counts and the out-of-range warnings possible at all.
 * A field renders as `<Field k="workers" …/>` and looks the same inside its own
 * card as it does in a search result.
 *
 * ── The reorganisation, in one paragraph ────────────────────────────
 * This was seven tabs named after internal subsystems, forty inline fields, and
 * a hint per field that ran to sixty words of reasoning at 11.5px in a colour
 * that fails contrast on a light background. Three of those seven tabs were
 * three different words for "look for the shift". The behaviour is untouched —
 * same fields, same units, same bounds, same payload — but the page now answers
 * three questions it previously could not: where is the setting I want, what
 * does this one do in one sentence, and what have I changed but not saved.
 */

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function SettingsPage() {
  const qc = useQueryClient()
  const uid = React.useId()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: HUNT_SETTINGS_KEY,
    queryFn: () => apiGet<HuntSettingsDto>('/api/automation/hunt-settings'),
    // The live block underneath is a running count, so this refreshes while the
    // page is open. Cheap: an in-memory read on the server, no database work.
    // Paused mid-save so a poll cannot start inside the write window and land
    // stale values on top of the result — see the mutation's `onMutate`.
    refetchInterval: (q) => (q.state.fetchStatus === 'paused' ? false : 2_000),
  })

  /*
   * Draft state, seeded from the server exactly once per load.
   *
   * The query refetches every two seconds for the live counters, and binding the
   * inputs straight to it would overwrite whatever the operator was halfway
   * through typing. The draft is therefore only synced when the form is not
   * dirty.
   */
  const [draft, setDraft] = React.useState<Draft | null>(null)
  /**
   * The values the draft was last SEEDED from — see `touched` below.
   *
   * Declared beside the draft it belongs to. The two are one piece of state:
   * what is in the boxes, and what was in them before anybody typed.
   */
  const seeded = React.useRef<Draft | null>(null)
  const [saved, setSaved] = React.useState(false)
  /*
   * Which section is showing. Local, and deliberately not persisted.
   *
   * The draft, the dirty check and the Save button are all shared across every
   * tab — this only decides what is rendered — so restoring a tab across reloads
   * would suggest the tabs are separate forms, which they are not.
   */
  const [tab, setTab] = React.useState<TabId>('market')
  const [query, setQuery] = React.useState('')
  const [listingChanges, setListingChanges] = React.useState(false)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const savedTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  React.useEffect(
    () => () => {
      if (savedTimer.current !== null) clearTimeout(savedTimer.current)
    },
    []
  )

  React.useEffect(() => {
    if (data && draft === null) {
      const next = draftFrom(data)
      seeded.current = next
      setDraft(next)
    }
  }, [data, draft])

  const save = useMutation({
    /*
     * Cancel the 2s poll before saving.
     *
     * The GET refetches every two seconds for the live counters. One already in
     * flight when Update is pressed carries the OLD values, and it lands AFTER
     * the mutation's cache write — so the form would snap back and Update would
     * re-enable itself next to the "Saved" tick, which reads exactly like the
     * save failed. Cancelling first means the stale response is discarded rather
     * than allowed to win.
     */
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: HUNT_SETTINGS_KEY })
    },
    mutationFn: (body: Partial<HuntSettingsDto>) =>
      apiPut<HuntSettingsDto>('/api/automation/hunt-settings', body),
    onSuccess: (next) => {
      qc.setQueryData(HUNT_SETTINGS_KEY, next)
      const seedNext = draftFrom(next)
      seeded.current = seedNext
      setDraft(seedNext)
      setSaved(true)
      setListingChanges(false)
      if (savedTimer.current !== null) clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setSaved(false), 2000)
    },
  })

  /*
   * Dirty against the SAME conversion the form was seeded with.
   *
   * Comparing to `draftFrom(data)` means a new field is dirty-checked the moment
   * it is seeded, and a unit can never be converted on one path and not the
   * other — the failure that leaves Update permanently lit, or permanently dead,
   * for one field.
   */
  const baseline = data === undefined ? null : draftFrom(data)

  /*
   * TWO different questions, which look like one and are not:
   *
   *   has the OPERATOR typed?    draft vs what they started from  (`touched`)
   *   is Update worth pressing?  draft vs what is stored now      (`dirty`)
   *
   * The first attempt at this used `dirty` for both. A change saved from another
   * console makes the draft differ from the server — which under that reading is
   * indistinguishable from somebody editing, so the re-sync below was suppressed
   * by the very condition it was meant to be triggered by. The two have to be
   * separate.
   */
  const touched =
    draft !== null &&
    seeded.current !== null &&
    (Object.keys(draft) as FieldKey[]).some(
      (k) => !same(draft[k], (seeded.current as Draft)[k])
    )

  const changed = draft !== null && baseline !== null ? changedKeys(draft, baseline) : []
  const dirty = changed.length > 0

  /*
   * Follow the server while the form is UNTOUCHED.
   *
   * The seed runs once, on `draft === null`, and nothing re-synced after it —
   * while `baseline` is recomputed from a query that refetches every two
   * seconds. So a change saved anywhere else made this form look dirty without
   * anyone touching it: the footer announced "Unsaved changes", Update lit up,
   * and pressing it wrote the stale values back over the change. With `market`
   * in the payload that revert also discards every stored session, costing a
   * fresh sign-in and an OTP per customer.
   *
   * Half-typed input is still never clobbered — that is what `touched` protects,
   * and a form somebody IS editing keeps their values and a live Update.
   */
  React.useEffect(() => {
    if (baseline === null || draft === null || touched) return
    const stale = (Object.keys(baseline) as FieldKey[]).some((k) => draft[k] !== baseline[k])
    if (!stale) return
    seeded.current = baseline
    setDraft(baseline)
  }, [baseline, draft, touched])

  const setField = React.useCallback((k: FieldKey, value: string) => {
    setDraft((d) => (d === null ? d : { ...d, [k]: value }))
  }, [])

  const commit = React.useCallback(() => {
    if (draft === null || !dirty || save.isPending) return
    save.mutate(toPayload(draft))
  }, [draft, dirty, save])

  const discard = React.useCallback(() => {
    if (baseline === null) return
    seeded.current = baseline
    setDraft(baseline)
    setListingChanges(false)
  }, [baseline])

  /*
   * Two shortcuts, and only two.
   *
   * Ctrl/Cmd+S saves: a settings page whose Update button is pinned to the
   * bottom of the viewport is still a page you have to move a pointer to, and
   * the browser's own Save-page dialog is not a thing anybody wants from a
   * console. `/` jumps to search, which is the fastest route to any of forty
   * fields — guarded so it only fires when the caret is not already in a field,
   * otherwise it would be impossible to type a slash anywhere on the page.
   */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        commit()
        return
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const el = document.activeElement
        const typing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement ||
          (el instanceof HTMLElement && el.isContentEditable)
        if (typing) return
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [commit])

  /*
   * A FAILED load must say so, not spin.
   *
   * This read `isLoading || !data`, which conflates "still fetching" with
   * "fetching finished and produced nothing". Once the retries were exhausted
   * the query settled with `isLoading: false` and `data: undefined`, so the
   * second clause held forever and the page showed "Loading settings…"
   * indefinitely — for what was actually a 404 it could have named outright.
   */
  if (error) {
    return (
      // The same scroller as the loaded state — a long upstream message is
      // otherwise clipped at the viewport edge with no way to read the rest of
      // it, which on an error screen is the whole content.
      <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
        <div className="mx-auto flex max-w-3xl flex-col gap-3 p-5">
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
            <TriangleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium">Could not load hunt settings</div>
              <div className="break-words opacity-90">{errorMessage(error)}</div>
            </div>
          </div>
          <div>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  /*
   * A skeleton of the page, not a spinner in the middle of an empty one.
   *
   * The shape that arrives is completely predictable — a header, a tab strip,
   * cards of rows — so drawing it while the request is in flight means the
   * content lands INTO a layout that is already there, instead of replacing a
   * centred spinner and shifting everything at once. It also tells you which
   * page you are on before the data says so.
   */
  if (isLoading || !data || draft === null || baseline === null) {
    return (
      <div className="flex min-h-0 flex-1 flex-col" aria-busy="true">
        <span role="status" className="sr-only">
          Loading hunt settings…
        </span>
        <div className="flex-none border-b border-line bg-s1">
          <div className="mx-auto max-w-3xl px-5 pt-3.5">
            <div className="flex items-center gap-2">
              <Radar aria-hidden="true" className="size-[18px] shrink-0 text-accent" />
              <h1 className="text-[15px] font-medium text-fg">Shift hunt</h1>
            </div>
            <div className="mt-3 flex items-end gap-4 border-b border-line pb-2.5">
              {['w-14', 'w-16', 'w-20', 'w-24', 'w-12', 'w-10'].map((w) => (
                <Skeleton key={w} className={cn('h-3.5', w)} />
              ))}
            </div>
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-5">
          {[0, 1].map((card) => (
            <div key={card} className="overflow-hidden rounded-lg border border-line bg-s1">
              <div className="border-b border-line px-4 py-3">
                <Skeleton className="h-3.5 w-32" />
              </div>
              <div className="divide-y divide-line/60">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="flex items-start gap-5 px-4 py-3.5">
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <Skeleton className="h-3.5 w-44" />
                      <Skeleton className="h-3 w-full max-w-[26rem]" />
                    </div>
                    <Skeleton className="h-8 w-[5.75rem] flex-none rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const idFor = (t: TabId) => ({ tab: `${uid}-tab-${t}`, panel: `${uid}-panel-${t}` })

  const counts: Partial<Record<TabId, number>> = {}
  for (const k of changed) {
    const t = FIELD_DEFS[k].tab
    counts[t] = (counts[t] ?? 0) + 1
  }

  const q = query.trim()
  const matches = q === '' ? [] : searchFields(q)

  const fieldProps = { draft, saved: baseline, onChange: setField }

  return (
    /*
     * This route owns its own scrolling, because `<main>` deliberately does not.
     *
     * The shell gives `<main>` a bounded `minmax(0, 1fr)` grid track and
     * `overflow-hidden`, so a route that returns a tall column is CLIPPED at the
     * viewport edge rather than scrolled. The max-width column sits INSIDE the
     * scroller, so the scrollbar belongs to the page and not to a 768px strip in
     * the middle of it.
     */
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        The header does not scroll, because it is the page's controls.

        Title, state, search and the section tabs used to be the first four
        things in the scroller, so three screens into the Brazil burst there was
        no way to see which section you were in, no way to reach another one,
        and no way to search without scrolling back to the top. A flex-none
        sibling of the scroller — the same shape the action bar already uses —
        pins them, and costs about ninety pixels to do it.
      */}
      <div className="flex-none border-b border-line bg-s1">
        <div className="mx-auto max-w-3xl px-5 pt-3.5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex min-w-0 items-center gap-2">
              <Radar aria-hidden="true" className="size-[18px] shrink-0 text-accent" />
              <h1 className="text-[15px] font-medium text-fg">Shift hunt</h1>
            </div>
            <AtAGlance data={data} />
          </div>

          {/*
            `flex-col-reverse` below `sm`: the search box is written second and
            therefore lands ABOVE the tabs on a phone, full width, while the
            tabs keep the whole row to scroll in. Side by side at 420px the two
            were fighting over about 380 pixels and the tabs lost — three of six
            visible, and the search field too narrow to read its own placeholder.
          */}
          <div className="mt-3 flex flex-col-reverse gap-2 border-b border-line sm:flex-row sm:items-end sm:gap-3">
            <SectionTabs value={tab} onChange={setTab} counts={counts} idFor={idFor} />

            {/*
              A search box, on a form of forty numbers across six sections.

              Without one the only way to find "the thing that buys captcha
              solves" was to remember which tab it was on, and the answer was a
              tab named after a country. Matching runs over the long
              explanation and an explicit keyword list as well as the label, so
              `429`, `capsolver`, `waf` and `postingStatus` all land somewhere.
            */}
            <Input
              ref={searchRef}
              icon={<Search aria-hidden="true" />}
              /*
               * `text`, not `search`. Chromium draws its own clear affordance
               * inside a search input, so the field ended up with two X buttons
               * side by side — one native, one the `Input` component's, which
               * is the one with a label and a tooltip.
               */
              type="text"
              value={query}
              placeholder="Search settings…"
              aria-label="Search hunt settings"
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              className={cn(
                'h-9 w-full flex-none md:h-8',
                // From `sm` up it is a compact box beside the tabs that widens
                // while it has focus, so a long query is still readable without
                // the field claiming that width for the whole session.
                'sm:mb-1.5 sm:w-[11rem] sm:transition-[width] sm:duration-150 sm:focus-within:w-[15rem]'
              )}
            />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 p-5 pb-10">
          {q !== '' ? (
            <SearchResults
              matches={matches}
              query={q}
              onClear={() => setQuery('')}
              fieldProps={fieldProps}
            />
          ) : (
            <>
              <p className="text-[12.5px] leading-[1.5] text-muted">
                {TABS.find((t) => t.id === tab)?.blurb}
              </p>

              <Panel id={idFor('market')} active={tab === 'market'}>
                <SettingCard
                  title="Country"
                  scope="run"
                  subtitle="The first decision, because it governs everything below it — the portal, the locale, the id prefixes and the background-check form."
                >
                  <Field k="market" {...fieldProps} />
                  <Field k="confirmMarket" {...fieldProps} />
                </SettingCard>

                {/*
                  Said plainly, because the cost is invisible and immediate:
                  every stored session belongs to the host it was minted on, so
                  changing this discards all of them at once. Nothing breaks —
                  the next Start signs in normally — but a hundred customers
                  each paying a fresh OTP is worth expecting rather than
                  discovering.
                */}
                {data.market !== draft.market ? (
                  <Note>
                    Switching to <strong>{marketLabel(draft.market)}</strong> points every call
                    after sign-in at <strong>{marketHost(draft.market)}</strong>. Sessions saved for
                    the old market cannot be reused there, so the next run for each customer signs
                    in again from scratch.
                  </Note>
                ) : null}

                {/*
                  Shown only when the two genuinely disagree — picking "Canada"
                  beside a Canadian jobs country is the same thing as "Follow",
                  and a warning about it would be noise. Says what does NOT
                  change as much as what does, because that is the part people
                  get wrong: this moves the host, never the ids.
                */}
                {draft.confirmMarket !== 'auto' && draft.confirmMarket !== draft.market ? (
                  <Note>
                    The shift confirm will leave through{' '}
                    <strong>{marketHost(draft.confirmMarket)}</strong> while everything else —
                    create, the schedule watch, the shift-list search — stays on{' '}
                    <strong>{marketHost(draft.market)}</strong>. The job and schedule ids do{' '}
                    <strong>not</strong> follow: they are still <code>JOB-{draft.market}-…</code> /{' '}
                    <code>SCH-{draft.market}-…</code>. Takes effect on the next run for each
                    customer, not on one already going.
                  </Note>
                ) : null}
              </Panel>

              <Panel id={idFor('find')} active={tab === 'find'}>
                <FindPanel draft={draft} data={data} fieldProps={fieldProps} />
              </Panel>

              <Panel id={idFor('claim')} active={tab === 'claim'}>
                <ClaimPanel draft={draft} savedFlow={data.huntFlow} fieldProps={fieldProps} />
              </Panel>

              <Panel id={idFor('after')} active={tab === 'after'}>
                <SettingCard
                  title="Orientation slot"
                  scope="run"
                  subtitle="Once a shift is won the application waits for an orientation slot to be published. Two boxes for one range: every applicant for a site is in that queue at once, so a single fixed gap has them all asking on the same tick."
                >
                  <Field k="nheMin" {...fieldProps} />
                  <Field k="nheMax" {...fieldProps} />
                </SettingCard>

                <SettingCard
                  title="Assessment"
                  scope="fleets"
                  subtitle="Amazon asks for a recorded assessment before the application can be submitted. Turned off, the run stops there and says a person is needed — without telling Amazon it was done."
                >
                  <Field k="assessmentEnabled" {...fieldProps} />
                </SettingCard>

                <SettingCard
                  title="Watchdog"
                  scope="run"
                  subtitle="When to abandon an onboarding run that has stopped moving."
                >
                  <Field k="watchdogMin" {...fieldProps} />
                </SettingCard>
              </Panel>

              <Panel id={idFor('limits')} active={tab === 'limits'}>
                <LimitsPanel draft={draft} fieldProps={fieldProps} />
              </Panel>

              {/*
                Browser-local, unlike everything around it — the sfx module owns
                these in localStorage/IndexedDB, so nothing on this panel
                participates in the draft, the dirty check or Update.
              */}
              <Panel id={idFor('sounds')} active={tab === 'sounds'}>
                <SoundsPanel />
              </Panel>

              {/*
                The one panel whose content is gated on being looked at.

                Every other panel is a fixed-size form and costs the same
                whether or not it is showing, so they stay mounted and their
                ids stay resolvable. This one grows with the number of live
                hunts and re-renders on the two-second poll, so at a hundred
                concurrent customers it would be a hundred rows redrawn every
                two seconds behind a tab nobody has open. The panel element
                still exists — only its list waits to be asked for.
              */}
              <Panel id={idFor('live')} active={tab === 'live'}>
                {tab === 'live' ? <HuntNowList live={data.live} /> : null}
              </Panel>
            </>
          )}
        </div>
      </div>

      <ActionBar
        dirty={dirty}
        changed={changed}
        draft={draft}
        baseline={baseline}
        pending={save.isPending}
        saved={saved}
        error={save.error}
        listing={listingChanges}
        onToggleList={() => setListingChanges((v) => !v)}
        onGoTo={(k) => {
          setQuery('')
          setTab(FIELD_DEFS[k].tab)
          setListingChanges(false)
        }}
        onSave={commit}
        onDiscard={discard}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Panels
 * ------------------------------------------------------------------ */

/**
 * A tab's contents.
 *
 * Every panel is rendered and the inactive ones are `hidden`, rather than the
 * active one being rendered and the rest returning null. That is what makes the
 * `aria-controls` on each tab point at an element that exists — a tablist whose
 * controls name ids that are not in the document is a tablist a screen reader
 * cannot follow.
 */
function Panel(p: {
  id: { tab: string; panel: string }
  active: boolean
  children: React.ReactNode
}) {
  return (
    <div
      role="tabpanel"
      id={p.id.panel}
      aria-labelledby={p.id.tab}
      hidden={!p.active}
      className={cn('flex-col gap-4', p.active ? 'panel-in flex' : 'hidden')}
    >
      {p.children}
    </div>
  )
}

type FieldProps = {
  draft: Draft
  saved: Draft
  onChange: (k: FieldKey, value: string) => void
}

/**
 * The fleets, as peers on one tab.
 *
 * They used to be three tabs — "Watching", "Searching", "Job record" — named
 * after the fleet behind each one, so the page asked the same question three
 * times in three different words and never said they were the same question.
 * Only the schedule workers watch for the opening and claim now; the GraphQL
 * and GA workers read the job's listings for a missing UUID, and the GA fleet
 * also runs before an application exists. The only thing an operator needs
 * from the layout is to see which are on.
 */
function FindPanel(p: { draft: Draft; data: HuntSettingsDto; fieldProps: FieldProps }) {
  const { draft, data, fieldProps } = p
  const watchers = Number(draft.workers) || 0
  /*
   * Floored at 1, like the server: this count sizes the fleet that reads a
   * missing UUID, and a run whose shift needs one must always have a reader. A
   * blank mid-edit box therefore reads as the floor, not as a fleet turned off.
   */
  const listWorkers = Math.max(1, Number(draft.gqlWorkers) || 1)
  /*
   * Floored at the FLOW's floor, which is zero on Brazil and one elsewhere.
   *
   * `Number('') || 0` is the idiom the watch above uses because zero is a
   * real state for them. It is now a real state here too, but only on the Brazil
   * flow — which arms no GA workers at all — so a blank mid-edit box still has
   * to read as the floor rather than as a fleet somebody turned off, and on the
   * current flow that floor is still one.
   */
  const gaFloor = draft.huntFlow === 'brazil' ? 0 : 1
  const gaWorkers = Math.max(gaFloor, Number(draft.gaWorkers) || gaFloor)
  const noDetector = watchers === 0

  /*
   * The day window as the operator reads it, and the night one it implies.
   *
   * Both ends come off the DRAFT, not the saved settings, so the sentence below
   * follows the boxes while they are being edited rather than describing a
   * window that is no longer on screen.
   */
  const dayStart = Number(draft.dayShiftStart)
  const dayEnd = Number(draft.dayShiftEnd)
  const emptyDayWindow = dayStart === dayEnd
  const dayWindow = `${clockLabel(dayStart)} to ${clockLabel(dayEnd)}`
  const nightWindow = `${clockLabel(dayEnd)} to ${clockLabel(dayStart)}`

  return (
    <>
      {/*
        The one condition the server refuses outright, said before it is hit.

        `workerCountsFor` rejects a customer who would arm with no detector at
        all, and the schedule workers are the only detector: the GraphQL and GA
        fleets read listings and never claim. A zero here is exactly the shape
        nobody notices they have typed, and the failure arrives much later as a
        hunt that will not start.
      */}
      {noDetector ? (
        <Note>
          Schedule workers are off, so nothing is watching for the shift — the GraphQL and GA
          workers only read listings and never claim. A hunt will refuse to arm.
        </Note>
      ) : null}

      {/*
        Its own card, because it is not a fleet.

        This sat as a fifth row under the schedule workers, where it read as a
        fifth thing they do. It is not: it runs for a customer who has no job
        and no shift yet, so it happens BEFORE any worker of any kind is armed,
        and its scope badge — "Whole run" among four "Schedule workers" — was
        the tell that the grouping was wrong.
      */}
      <SettingCard
        title="Picking a shift to hunt"
        scope="run"
        subtitle="Runs before any worker starts: a customer with no job and no shift is matched against the schedule list until something fits."
      >
        <Field k="matchPoll" {...fieldProps} />
        <Field k="repickSeatsGone" {...fieldProps} />
        {/*
          Two notes, on the two facts an operator will not predict from the row.
          On Brazil it fires often: the pick gate and the watch's reads look at
          the same number, so a shift that qualifies at the pick very often
          stops qualifying minutes later, and the fleet spends more time parked.
          That is the intent, not a fault, and it reads as a fault unsaid. On
          Current it does nothing at all — the give-up rules live on the Claim
          tab — and a switch that changes nothing needs to say so where it sits.
        */}
        {draft.huntFlow !== 'brazil' ? (
          <Derived>
            The fleet is on the Current flow, which does not read this: it gives a shift up on the
            two settings under{' '}
            <strong className="text-fg">Claiming → When to give the shift up</strong>, fed by what
            the schedule workers read. This switch still reaches a customer put on the Brazil flow
            by a per-customer override.
          </Derived>
        ) : draft.repickSeatsGone === 'on' ? (
          <Derived>
            Very few shifts carry a free seat at any moment, so an automatically picked shift
            filling up shortly afterwards is the ordinary case. A burst that ends with the schedule
            workers reading no free seat sends that customer back to the picker — expect most AUTO
            customers to go back to waiting rather than hold a shift with no room. How long they
            wait before picking again is set on the Claim tab, under “wait before looking for
            another shift”.
          </Derived>
        ) : null}
        <Field k="dayShiftStart" {...fieldProps} />
        <Field k="dayShiftEnd" {...fieldProps} />
        {/*
          Night is never typed, only left over — so it is shown rather than
          asked for. Two boxes that between them define a fourth boundary the
          operator cannot see is how a window gets set backwards.
        */}
        {emptyDayWindow ? (
          <Note>
            Day shifts start and end at the same time, which leaves no day window at all. Night is
            whatever the day window does not cover, so this will be refused — move one of them.
          </Note>
        ) : (
          <Derived>
            Full-time shifts starting {dayWindow} count as <strong>day</strong> shifts; those
            starting {nightWindow} count as <strong>night</strong>. Matched against each customer’s
            own shift priority, by the time the shift starts. Customers set to “Any”, and all
            part-time and flex customers, are not filtered on it.
          </Derived>
        )}
      </SettingCard>

      <SettingCard
        title="Schedule workers"
        scope="schedule"
        status={<OnOff on={watchers > 0} detail={`${watchers} per customer`} />}
        subtitle="The primary fleet: reads one schedule’s status, over and over, and claims the moment it reports open."
      >
        <Field k="workers" {...fieldProps} />
        <Field k="accounts" {...fieldProps} />
        <Field k="rotate403" {...fieldProps} />
        <Field k="interval" {...fieldProps} />
      </SettingCard>

      <WatchRateLine hunts={data.live.hunts} workers={watchers} gapMs={Number(draft.interval) || 0} />

      <SettingCard
        title="GraphQL workers"
        scope="graphql"
        status={<OnOff on detail={`${listWorkers} per customer · only while a shift has no UUID`} />}
        subtitle="On the Current flow these run only while the assigned shift has no UUID: they read the job’s shift list to find it, then stand down. They never claim. The count sizes that fleet and is floored at 1. Unauthenticated, so each costs an address and no account."
      >
        <Field k="gqlWorkers" {...fieldProps} />
      </SettingCard>

      {/*
        The fleet with one job on the Current flow: while the assigned shift
        has no UUID it reads the job's listing beside the GraphQL workers to
        find it, then stands down. It used to run at a different TIME from the
        two above as well — before an application existed, waiting for the job
        to advertise anything at all so one could be created. The create now
        goes out at Start and is retried if refused, with no worker armed until
        it takes. Its card carries no claim settings: it does not claim.
      */}
      {/*
        The badge can now read "Off", which it could not before.

        `OnOff` renders that word alone and drops the detail, which is right
        here: WHY it is off is a sentence about the flow, and that belongs in
        the subtitle where an operator is already reading.
      */}
      <SettingCard
        title="GA workers"
        scope="ga"
        status={<OnOff on={gaWorkers > 0} detail={`${gaWorkers} per customer`} />}
        subtitle="Reads the job's schedule list while the assigned shift has no UUID, beside the GraphQL workers, to read the UUID off the listing — then stands down. On the Current flow it cannot be turned off, since a shift with no UUID needs at least one reader. The Brazil flow never runs it for an ordinary run, so there it can be set to 0. It never claims, and nothing runs before an application exists."
      >
        <Field k="gaWorkers" {...fieldProps} />
        <Field k="gaAccounts" {...fieldProps} />
        <Field k="gaPollMs" {...fieldProps} />
      </SettingCard>

      {/*
        Both budgets, and both reset tools, directly under the cards that spend
        them. An operator looking at "pool has 3 free" is exactly the operator
        who needs the release button, and putting the control anywhere else
        would mean finding it while reading a figure somewhere else.

        ── Why two pools and not one ───────────────────────────────────
        They are different resources with different arithmetic and either can
        be the ceiling you actually hit. A customer spends ACCOUNTS at
        `workers × accounts`, and only the schedule and GA fleets spend them
        at all. It spends ADDRESSES at one per worker of every fleet plus one
        for its own session — so turning on the GraphQL fleet costs nothing
        from the account pool and one address per worker from this one. The
        page showed only the first, which is how a fleet gets sized against a
        budget that was never the binding constraint.
      */}
      <SettingCard
        title="Shared pools"
        scope="run"
        subtitle="Two finite resources, both shared with the other system, and either can be what stops another customer starting."
      >
        <div className="flex flex-col gap-2 px-4 py-3">
          <h3 className="text-[12px] font-medium text-fg">Session accounts</h3>
          <PoolBudgetLine
            pool={data.pool}
            watchWorkers={watchers}
            watchAccounts={Number(draft.accounts) || 0}
            gaWorkers={gaWorkers}
            gaAccounts={Number(draft.gaAccounts) || 0}
          />
          <ReleasePoolTool pool={data.pool} />
        </div>

        <div className="flex flex-col gap-2 px-4 py-3">
          <h3 className="text-[12px] font-medium text-fg">ISP endpoints</h3>
          <IspBudgetLine
            pool={data.ispPool}
            watchWorkers={watchers}
            gqlWorkers={listWorkers}
            gaWorkers={gaWorkers}
          />
          <ReleaseIspTool pool={data.ispPool} />
        </div>
      </SettingCard>

      {watchers >= 10 ? (
        <Note>
          Each worker claims its own ISP endpoint for the whole hunt, so{' '}
          <strong>{watchers} workers × concurrent customers</strong> must fit inside the pool.
          Beyond that, workers fall back to sharing the customer&rsquo;s own address.
        </Note>
      ) : null}

      <Derived className="rounded-md border border-line">
        How much of each call these fleets keep is set together, under{' '}
        <strong className="text-fg">Limits → Call logs</strong> — every fleet stages
        into one Redis budget, so they are worth reading side by side.
      </Derived>
    </>
  )
}

/** Whether a detector is on, in words and in colour — never in colour alone. */
function OnOff(p: { on: boolean; detail: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-1.5 py-px text-[11px] font-medium',
        p.on ? 'bg-ok-soft text-ok' : 'bg-s3 text-muted'
      )}
    >
      <span
        aria-hidden="true"
        className={cn('size-[6px] rounded-full', p.on ? 'bg-current' : 'bg-current opacity-50')}
      />
      {p.on ? `On · ${p.detail}` : 'Off'}
    </span>
  )
}

function ClaimPanel(p: { draft: Draft; savedFlow: string; fieldProps: FieldProps }) {
  const { draft, savedFlow, fieldProps } = p
  const burst = draft.huntFlow === 'brazil'
  const perPass = Math.max(1, (Number(draft.burstCreates) || 0) + (Number(draft.burstUpdates) || 0))
  const maxCalls = perPass * Math.max(1, Number(draft.burstMaxLoops) || 0)
  const fleetAtOnce = Number(draft.burstFleetConcurrency) || 0

  return (
    <>
      {/*
        FIRST on this tab, because it decides what every other setting on it
        means — and, unlike them, it changes what START does as well as what the
        claim does.
      */}
      <SettingCard
        title="How the claim is made"
        scope="fleets"
        subtitle="A schedule reports ACTIVE roughly 60–240 seconds before the seat can actually be granted. That gap is the whole reason there are two answers here."
      >
        <Field k="huntFlow" {...fieldProps} />
        <Field k="brazilUuidMissing" {...fieldProps} />
        {/*
          Both of these fire on the setting the operator is LOOKING at rather
          than on the saved one, because the whole point is to say what they are
          about to choose before they choose it. Neither is a warning about a
          mistake — both options are legitimate — so they say what the cost is
          and leave the decision alone.
        */}
        {draft.brazilUuidMissing === 'numeric' ? (
          <Derived>
            <span className="text-warn">
              Runs will send the old numeric ids for any shift with no UUID. Those calls do not
              error — they simply stop winning, and every counter on the panel keeps reading
              healthy. Right while proving the new form against the live service; wrong after.
            </span>
          </Derived>
        ) : null}
        {draft.brazilUuidMissing === 'park' ? (
          <Derived>
            A parked run holds its session and its pinned address for the whole wait, which has
            measured anywhere from ten minutes to an hour and a half. Shifts the schedule
            collection has never heard of are stopped rather than parked — that wait could not end.
          </Derived>
        ) : null}
        <Field k="brazilNoUuid" {...fieldProps} />
        {/*
          Fires on the setting being LOOKED AT, like the two above, and for the
          same reason: it says what is about to be turned on before it is. The
          warning half is real — two of the requests have never been seen
          accepted — and it sits beside what the setting buys, because refusing
          four fifths of the inventory is also a cost.
        */}
        {draft.brazilNoUuid === 'on' ? (
          <Derived>
            <span className="text-warn">
              Automatically picked shifts may now be ones with no shift UUID, hunted with two
              requests nobody has seen this service accept: a create naming the job and no shift,
              and a confirm naming the job by UUID. Shifts that already have a UUID are still
              preferred, and nothing reaches a customer with a job and shift set on their row.
              Prove it on one customer first.
            </span>
          </Derived>
        ) : null}
        <Field k="currentUuid" {...fieldProps} />
        {/*
          The OFF note, not the on one — the opposite of its three neighbours,
          and deliberately so. Off is the state worth a line here: it is the old
          numeric request, which is the same thing `brazilUuidMissing: 'numeric'`
          is warned about a few lines up, and for the same reason. On is now the
          default and needs no explaining beyond the field's own copy.
        */}
        {draft.currentUuid === 'off' ? (
          <Derived>
            <span className="text-warn">
              Current-flow runs will name the job and shift by number on every call. That is what
              this system sent before UUIDs existed and it is the right setting while proving the
              new form — but the endpoints are moving to the UUID, so leaving it here indefinitely
              is the same quiet loss of wins as falling back above.
            </span>
          </Derived>
        ) : null}
        {draft.currentUuid === 'on' && draft.huntFlow !== 'current' ? (
          <Derived>
            The fleet is on the Brazil flow, so this changes nothing today — it governs the Current
            flow only. It still applies to any customer put on the Current flow by a per-customer
            override.
          </Derived>
        ) : null}
      </SettingCard>

      {/*
        Said before it is saved, because the surprising half is not the claim.
        Switching to the burst stops Start from creating an application at all,
        which changes what a healthy run looks like from its very first line — a
        drawer that used to say "Application created." goes quiet until a shift
        opens. An operator who has not been told that reads it as a broken run.
      */}
      {burst && savedFlow !== 'brazil' ? (
        <Note>
          The <strong>Brazil</strong> flow files no application at Start. It creates one when a
          shift opens — on that endpoint the create and the grant are the same call. Hunts already
          running keep the flow they armed with; this reaches a customer on their next Start.
        </Note>
      ) : null}

      {burst ? (
        <SettingCard
          title="The Brazil burst"
          scope="fleets"
          subtitle="Wait out the early-warning window, then work the endpoint in a bounded loop. Every call in a burst leaves from this customer’s one pinned address."
        >
          <Field k="brazilRefererIds" {...fieldProps} />
          <Field k="burstWaitSec" {...fieldProps} />
          <Field k="burstCreates" {...fieldProps} />
          <Field k="burstUpdates" {...fieldProps} />
          <Field k="burstMaxLoops" {...fieldProps} />
          <Field k="burstGapMs" {...fieldProps} />
          <BurstCostLine
            waitSec={Number(draft.burstWaitSec)}
            loops={Number(draft.burstMaxLoops)}
            creates={Number(draft.burstCreates)}
            confirms={Number(draft.burstUpdates)}
            gapMs={Number(draft.burstGapMs)}
          />
          <Field k="burstCreateAttempts" {...fieldProps} />
          <Field k="burstUnavailableMax" {...fieldProps} />
          {Number(draft.burstUnavailableMax) === 0 ? (
            <Derived>
              <span className="text-warn">
                A burst will never stand down on a refused seat. It will keep confirming for the
                whole opening — up to {maxCalls} calls from one pinned address — for an answer it
                has already been given.
              </span>
            </Derived>
          ) : null}
          <Field k="burstUnavailableCooldownSec" {...fieldProps} />
          <Field k="burstReassignDelaySec" {...fieldProps} />
          {/*
            Not a warning — 0 is the shipped value and the behaviour that was
            asked for. It says what 0 costs, because the picker is uniform over
            every match and the schedule list is slower to drop a shift than the
            claim endpoint is to refuse one, so the shift just lost is a live
            candidate again immediately.
          */}
          {Number(draft.burstReassignDelaySec) === 0 ? (
            <Derived>
              A lost shift is looked for again on the next check, and it is not excluded from the
              search — the schedule list often still shows seats on a shift the claim has just
              refused, so the same one may well be picked again. Raise this to put a gap between
              attempts.
            </Derived>
          ) : null}
          <Field k="burstSolveBudget" {...fieldProps} />
          {Number(draft.burstSolveBudget) === 0 ? (
            <Derived>
              <span className="text-warn">
                No limit. A burst that keeps getting challenged will keep buying solves for as long
                as it runs — up to {maxCalls} of them, per customer, per opening. Set a number
                unless you are deliberately testing.
              </span>
            </Derived>
          ) : null}
          <Field k="burstLogEvery" {...fieldProps} />
          <Field k="burstFleetConcurrency" {...fieldProps} />
          {fleetAtOnce > 0 && fleetAtOnce < 30 ? (
            <Derived>
              <span className="text-warn">
                Low. One customer bursting uses up to {perPass} of these at a time, so this holds
                roughly {Math.max(1, Math.floor(fleetAtOnce / perPass))} customers at full speed.
                Beyond that they queue — safe, but slower on a shift everyone wants.
              </span>
            </Derived>
          ) : null}
        </SettingCard>
      ) : (
        <Derived className="rounded-md border border-line">
          The <strong className="text-fg">Brazil</strong> burst has eleven settings of its own — the
          wait, the loop, the solve budget, the fleet limit. They appear here when the claim flow
          above is set to <strong className="text-fg">Brazil</strong>.
        </Derived>
      )}

      {/*
        Its own card rather than six more rows in the burst above, because these
        describe a DIFFERENT sequence — not a slower or faster version of that
        one. Shown only once the switch is on: while it is off no run can take
        this path, and six dials for a thing that cannot happen is noise on a
        tab that already has thirty.
      */}
      {burst && draft.brazilNoUuid === 'on' ? (
        <SettingCard
          title="Shifts with no UUID"
          scope="fleets"
          subtitle="A run that was picked onto a shift the schedule poller has not resolved yet works the endpoint on the job alone: one create naming no shift, then confirms, while GraphQL and GA workers — the Finding tab’s counts, at least one of each — read the job’s listings for the shift’s UUID. The moment one of them finds it, the run switches to the burst above."
        >
          <Field k="noUuidWaitSec" {...fieldProps} />
          <Field k="noUuidGapMinMs" {...fieldProps} />
          <Field k="noUuidGapMaxMs" {...fieldProps} />
          <NoUuidRateLine
            minMs={Number(draft.noUuidGapMinMs)}
            maxMs={Number(draft.noUuidGapMaxMs)}
          />
          <Field k="noUuidUnavailableMax" {...fieldProps} />
          {Number(draft.noUuidUnavailableMax) > 0 ? (
            <Derived>
              <span className="text-warn">
                These runs will stand down partway through the pre-open window. That reply is what
                the endpoint says for the whole of it, so a low number here ends the sequence
                before it has had a chance to work.
              </span>
            </Derived>
          ) : null}
        </SettingCard>
      ) : null}

      {/*
        Rendered on both flows, like the card after it. The flow can be
        overridden per customer, so a fleet on the Brazil flow can still have
        customers on the Current one — and this is the setting their first
        claims obey.
      */}
      <SettingCard
        title="When to start claiming"
        subtitle="A schedule reports ACTIVE roughly 60–240 seconds before the seat can actually be granted. The first ACTIVE a schedule worker sees still fires one confirm; this holds every further confirm back for the first part of that window, measured from that sighting, once per hunt."
        scope="schedule"
      >
        <Field k="firstActiveHoldSec" {...fieldProps} />
        {draft.huntFlow !== 'current' ? (
          <Derived>
            The fleet is on the Brazil flow, so this changes nothing today — it governs the Current
            flow only, which the Brazil burst’s own wait replaces. It still applies to any customer
            put on the Current flow by a per-customer override.
          </Derived>
        ) : null}
      </SettingCard>

      {/*
        Two cards for two different decisions, which used to be one.

        "Stop claiming" bounds one OPENING: a schedule reporting open for tens
        of seconds while the edge refuses everything, where every read is a new
        sighting and would be a new claim. Claiming resumes on the next opening.
        "Give the shift up" ends an AUTO-PICKED hunt's interest in the shift
        altogether — the seat has said no enough times over the whole run, or
        the schedule workers have read it full for long enough — and what
        follows is a re-pick. A pair somebody set is never given up: the first
        of those numbers only pauses its confirms per opening, and the second
        does not apply. Reading the two sets of numbers as one list was how a
        per-opening cap got tuned as if it were the give-up rule.
      */}
      <SettingCard
        title="When to stop claiming"
        scope="schedule"
        subtitle="A schedule can report open for tens of seconds while every claim fails. These bound one opening: the first stops the schedule workers claiming after so many refusals in a row (HTTP 429, 403 or 502), and the second says how fast they may ask meanwhile. Only schedule workers claim, so nothing else is held down by either."
      >
        <Field k="failedUpdateStreakMax" {...fieldProps} />
        <Field k="claimGapMs" {...fieldProps} />
        <ClaimCostLine
          cap={Number(draft.failedUpdateStreakMax)}
          gapMs={Number(draft.claimGapMs)}
        />
        <Derived>
          Both clear when the schedule reads closed, so the next opening is claimed instantly
          again. Neither gives the shift up — that is the card below.
        </Derived>
      </SettingCard>

      {/*
        Its own card, not a third row above. The stand-down bounds one OPENING
        and ends when the schedule reads closed; this counts 429s across
        openings, because the throttle is on the account, and what it ends in is
        the whole run going quiet for a while — then a fresh apply.
      */}
      <SettingCard
        title="When Amazon rate-limits the claims"
        scope="run"
        subtitle="A run of 429s means Amazon is throttling this account, and every further claim keeps it going. After this many in a row the customer is parked: no claims, no schedule reads and no heartbeat. When the park ends, a job and shift set on the customer are applied to again; otherwise the picker chooses a new shift."
      >
        <Field k="rateLimitParkAfter" {...fieldProps} />
        <Field k="rateLimitParkMin" {...fieldProps} />
        {draft.huntFlow !== 'current' ? (
          <Derived>
            The fleet is on the Brazil flow, so this changes nothing today — it governs the Current
            flow only. It still applies to any customer put on the Current flow by a per-customer
            override.
          </Derived>
        ) : Number(draft.rateLimitParkAfter) === 0 ? (
          <Derived>
            <span className="text-warn">
              Off: a rate-limited customer keeps claiming on every sighting for as long as Amazon
              keeps answering 429.
            </span>
          </Derived>
        ) : (
          <Derived>
            A shift closing and reopening does not reset the count; another answer to a claim does.
          </Derived>
        )}
      </SettingCard>

      <SettingCard
        title="When to give the shift up"
        scope="schedule"
        subtitle="The two ways an auto-picked shift is judged gone: the seat has said “not available” this many times over the whole hunt, or the schedule workers have read it with no free seat this many times in a row. The customer then looks for another shift. A pair set on the customer is never given up: the first setting only pauses its confirms until the schedule closes and reads ACTIVE again, counted per opening, and the second does not apply to it."
      >
        <Field k="claimUnavailableMax" {...fieldProps} />
        <Field k="seatEmptyReads" {...fieldProps} />
        {draft.huntFlow !== 'current' ? (
          <Derived>
            The fleet is on the Brazil flow, so neither changes anything today — both govern the
            Current flow only. The burst has its own refusal bound, and gives a shift up on “Give up
            a shift once it fills” on the Finding tab. Both still apply to any customer put on the
            Current flow by a per-customer override.
          </Derived>
        ) : Number(draft.claimUnavailableMax) === 0 && Number(draft.seatEmptyReads) === 0 ? (
          <Derived>
            <span className="text-warn">
              Both off: a Current-flow run never gives its shift up on its own. An auto-picked
              customer keeps hunting a shift that has refused every claim and reads full, and
              nothing sends them back to the picker. An assigned pair claims on every sighting,
              whatever the seat says.
            </span>
          </Derived>
        ) : null}
      </SettingCard>

      {/*
        Its own card, deliberately not another row under "When to stop claiming".

        Both are gaps in milliseconds and would be indistinguishable side by
        side, but they govern different calls on different customers. The claim
        gap spaces a confirm on a shift that is open right now; this one spaces a
        poll that only ever runs for a customer Amazon has REFUSED to create an
        application for. A heading is the cheapest way to stop one being tuned as
        the other.
      */}
      <SettingCard
        title="Identity verification"
        scope="run"
        subtitle="Runs only when Amazon blocks a customer’s application until they have proved who they are."
      >
        <Field k="idvLinkGapMs" {...fieldProps} />
        {Number(draft.idvLinkGapMs) === 0 ? (
          <Derived>
            At <strong className="tabular text-fg">0</strong> this asks again as fast as answers come
            back — one bot-check solve per round trip if the endpoint is challenging, on an address
            shared with the other system, and one run-log line per attempt. Raise it if 403s or
            solve spend climb.
          </Derived>
        ) : null}
      </SettingCard>
    </>
  )
}

function LimitsPanel(p: { draft: Draft; fieldProps: FieldProps }) {
  const { draft, fieldProps } = p
  const maxHours = Number(draft.maxHours)
  const keepAlive = Number(draft.keepAliveSec)

  return (
    <>
      <SettingCard
        title="How long a run lives"
        scope="run"
        subtitle="The ceiling on one hunt. A hunt that reaches it looks exactly like one that was stopped, which is the only reason this is on screen at all."
      >
        <Field k="maxHours" {...fieldProps} />
        {/*
          Said plainly, because the failure it prevents is silent and slow: a
          hunt that simply stops after N hours with the shift never claimed, and
          a log line hours in the past explaining why.
        */}
        {maxHours > 0 && maxHours < 12 ? (
          <Derived>
            <span className="text-warn">
              Hunts will give up after {maxHours}h. For sessions meant to run all day, set this to 0
              (never stop) or raise it past the longest run you expect.
            </span>
          </Derived>
        ) : null}
      </SettingCard>

      {/*
        The heartbeat and the two session fields are one story and are read as
        one: the heartbeat is what keeps a session inside the ~60s anti-bot
        window, and the pair below is what keeps the bearer token behind it
        alive. Tuning any one of them without the other two in view is how a run
        ends up beating perfectly against a token that died an hour ago.
      */}
      <SettingCard
        title="Holding the seat"
        scope="run"
        subtitle="One story in three numbers: the beat that holds the seat and keeps the session warm, and the sign-in behind it that cannot be refreshed."
      >
        <Field k="keepAliveSec" {...fieldProps} />
        {/*
          What each end of the range costs, said where it is chosen — and
          NEITHER is enforced. The number typed here is the number that runs.
          Two different notes because they are two different mistakes: 0 is a
          deliberate posture with a delayed, invisible cost; a very low interval
          is usually a typo with an immediate, shared one.
        */}
        {keepAlive === 0 ? (
          <Derived>
            <span className="text-warn">
              The seat is never re-confirmed and sessions drift out of the anti-bot window — so the
              claim may have to solve a challenge at the one moment that cannot afford it. Hunts
              already running keep their address and keep polling; nothing is stopped.
            </span>
          </Derived>
        ) : keepAlive > 0 && keepAlive < 10 ? (
          <Derived>
            At <strong className="tabular text-fg">{draft.keepAliveSec}s</strong> every customer
            sends a call this often, forever, on the same endpoint the claim uses — over metered
            residential addresses, with a solve bought whenever one is challenged. The window it
            protects is about <strong className="text-fg">60s</strong> wide, so anything much below
            that buys load rather than safety.
          </Derived>
        ) : null}

        <Field k="sessionMaxMin" {...fieldProps} />
        <Field k="sessionRenewMin" {...fieldProps} />
        <RenewHeadroomNote
          lifetimeMin={Number(draft.sessionMaxMin)}
          renewMin={Number(draft.sessionRenewMin)}
        />
      </SettingCard>

      {/*
        Both log depths in one card, which they were not.

        The shift list's depth used to appear on two different tabs under two
        different explanations — two halves of one Redis budget, never once
        shown together. They share a batcher that drains about a thousand
        commands per 200ms, so the only useful way to set either is beside the
        other.
      */}
      <SettingCard
        title="Call logs"
        subtitle="How much of its own history each worker keeps, for the run drawer. Every fleet stages into one Redis budget, so these are set together."
      >
        <Field k="logCalls" {...fieldProps} />
        <Field k="gqlLogCalls" {...fieldProps} />
      </SettingCard>

      <SettingCard
        title="Resuming after a deploy"
        scope="run"
        subtitle="A deploy restarts the server and every running customer stops with it. The Sessions view keeps a roster of who was running; this is the pace it starts them again at."
      >
        <Field k="resumeStaggerMs" {...fieldProps} />
        <ResumePaceNote staggerMs={Number(draft.resumeStaggerMs)} />
      </SettingCard>
    </>
  )
}

/**
 * What the chosen gap costs at fleet scale.
 *
 * The number itself is meaningless until it is multiplied by a roster — 200ms
 * reads as instant and is fifteen seconds across seventy-five customers — so
 * the arithmetic is done here rather than left to the operator at the moment
 * they are deciding whether the fleet comes back fast enough.
 */
function ResumePaceNote(p: { staggerMs: number }) {
  if (!Number.isFinite(p.staggerMs) || p.staggerMs <= 0) {
    return (
      <Derived>
        Customers are launched back-to-back. The starts are still sequential, and the number of
        sign-ins actually running at once is capped separately.
      </Derived>
    )
  }
  const seconds = (p.staggerMs * 75) / 1000
  const label = seconds >= 90 ? `${(seconds / 60).toFixed(1)} min` : `${Math.round(seconds)}s`
  return (
    <Derived>
      A roster of 75 customers takes about <strong className="tabular text-fg">{label}</strong> to
      launch end to end.
    </Derived>
  )
}

/* ------------------------------------------------------------------ *
 * At a glance
 * ------------------------------------------------------------------ */

/**
 * What this console is set to, in one line, above everything.
 *
 * Six sections mean the answer to "what is this configured to do" was
 * previously six clicks. These are the four facts that change how every other
 * number on the page should be read, and they come from the SAVED settings
 * rather than the draft — this says what is running, not what is being typed.
 */
function AtAGlance({ data }: { data: HuntSettingsDto }) {
  const confirm =
    data.confirmMarket === 'auto'
      ? 'same portal'
      : `via ${marketHost(data.confirmMarket)}`
  /*
   * Named the way the fleets are named everywhere else on the page.
   *
   * This read "watch 5 · list 3", which is two more words for fleets that
   * already have names — and both were the old tab titles this reorganisation
   * removed.
   */
  const detectors = [
    data.workersPerCustomer > 0 ? `schedule ${data.workersPerCustomer}` : null,
    // Floored at 1 on the server: it sizes the fleet that reads a missing UUID,
    // so it is never off and never conditional here.
    `graphql ${Math.max(1, data.graphqlWorkersPerCustomer)}`,
    // Never conditional: this fleet has no off, so a missing clause here would
    // read as "not running" for a fleet that always is.
    `GA ${data.schedulesWorkersPerCustomer}`,
  ].filter((x): x is string => x !== null)

  return (
    /*
     * Inline in the header rather than a card of its own.
     *
     * As a bordered strip under the title it read as a fifth thing to deal
     * with, and it is not — it is the page's subtitle, in facts instead of
     * prose. Four chips on the title row say what the console is set to without
     * asking for any of the room a panel would.
     */
    <dl className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 sm:ml-auto">
      <Glance term="Market">
        {marketLabel(data.market)} · {confirm}
      </Glance>
      <Glance term="Flow">
        {data.huntFlow === 'brazil' ? 'Brazil — burst' : 'Current — one confirm'}
      </Glance>
      <Glance term="Fleets" warn={detectors.length === 0}>
        {detectors.length > 0 ? detectors.join(' · ') : 'none on'}
      </Glance>
      <Glance term="Pool">{data.pool.ready ? `${data.pool.free} free` : 'not ready'}</Glance>
    </dl>
  )
}

function Glance(p: { term: string; children: React.ReactNode; warn?: boolean }) {
  return (
    <div
      className={cn(
        // `s3`, not `s2`: in the light palette `--s2` is pure white, which is
        // also the header's own ground, so the chip had no shape at all there.
        'flex min-w-0 items-baseline gap-1.5 rounded-md px-2 py-1',
        p.warn ? 'bg-warn-soft' : 'bg-s3'
      )}
    >
      <dt className="text-[10.5px] tracking-[0.04em] text-muted uppercase">{p.term}</dt>
      <dd className={cn('truncate text-[12px]', p.warn ? 'text-warn' : 'text-fg')}>
        {p.children}
      </dd>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Search results
 * ------------------------------------------------------------------ */

function SearchResults(p: {
  matches: FieldKey[]
  query: string
  onClear: () => void
  fieldProps: FieldProps
}) {
  if (p.matches.length === 0) {
    return (
      <div className="rounded-lg border border-line bg-s1 px-4 py-8 text-center">
        <p className="text-[13px] text-fg">
          Nothing matches &ldquo;<span className="text-muted">{p.query}</span>&rdquo;
        </p>
        <p className="mt-1 text-[12.5px] text-muted">
          Search runs over every label, explanation and keyword on the page.
        </p>
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={p.onClear}>
            Back to all sections
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p role="status" className="text-[12.5px] text-muted">
        <strong className="tabular text-fg">{p.matches.length}</strong> setting
        {p.matches.length === 1 ? '' : 's'} match &ldquo;{p.query}&rdquo;. Editing one here is the
        same as editing it in its own section.
      </p>
      <section className="overflow-hidden rounded-lg border border-line bg-s1">
        <div className="divide-y divide-line/60">
          {p.matches.map((k) => (
            <Field key={k} k={k} showLocation {...p.fieldProps} />
          ))}
        </div>
      </section>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The action bar
 * ------------------------------------------------------------------ */

/**
 * The action bar is a footer of the ROUTE, outside the scroller.
 *
 * It commits every section, so living inside one of them was wrong the moment
 * there were two — and it has to stay reachable, because a change made in the
 * first section is otherwise two scrolls away from the button that saves it.
 *
 * Outside rather than `position: sticky` inside, which was the first attempt and
 * is subtly wrong here: a sticky footer with content after it overlays that
 * content the whole way down and then jumps back to its natural position at full
 * scroll. A flex sibling of the scroller is always exactly where it looks like
 * it is, and never covers a row.
 *
 * ── Why it lists what changed ───────────────────────────────────────
 * The old footer knew the form was dirty and could not say which field, or even
 * which section, was responsible — and with six sections and forty numbers,
 * "Unsaved changes" is a sentence you cannot act on. Each row names the setting,
 * the old value and the new one, and clicking it goes to the section it lives
 * in.
 */
function ActionBar(p: {
  dirty: boolean
  changed: FieldKey[]
  draft: Draft
  baseline: Draft
  pending: boolean
  saved: boolean
  error: unknown
  listing: boolean
  onToggleList: () => void
  onGoTo: (k: FieldKey) => void
  onSave: () => void
  onDiscard: () => void
}) {
  const listing = p.listing && p.dirty

  /*
   * The bar exists only when it has something to say.
   *
   * It used to be permanent: a greyed-out Update button and a full-width rule
   * across the bottom of every visit, including the overwhelming majority that
   * are somebody reading a number rather than changing one. A save bar with
   * nothing to save is chrome that never earns its forty pixels, and worse, it
   * makes the moment there IS something to save look exactly like the moment
   * there is not.
   *
   * So it slides in on the first edit and leaves after the save has been
   * acknowledged. Nothing is hidden by this — with no unsaved change, Update
   * would have been disabled anyway.
   */
  const show = p.dirty || p.pending || p.saved || Boolean(p.error)

  /*
   * Escape closes the list.
   *
   * It is dismissed by a full-bleed transparent backdrop, and a backdrop that
   * only answers to a click is a panel a keyboard user cannot put away — they
   * would have to tab back to the toggle that opened it, past everything the
   * backdrop is covering.
   */
  const { onToggleList } = p
  React.useEffect(() => {
    if (!listing) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggleList()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [listing, onToggleList])

  // After every hook, never before one.
  if (!show) return null

  return (
    <div
      className={cn(
        'relative flex-none border-t border-line bg-s1 shadow-e2',
        // Slides up on the first edit. The reduced-motion rule in theme.css
        // collapses this to nothing for anyone who has asked for that.
        'transition-[transform,opacity] duration-150 ease-out',
        'starting:translate-y-full starting:opacity-0'
      )}
    >
      {listing ? (
        <>
          {/*
            Click anywhere else to dismiss.

            Hidden from assistive tech and out of the tab order deliberately: it
            is a click surface, not a control, and a full-screen invisible
            button sitting in the tab order between the footer and the form is
            a stop nobody can see the purpose of. Escape does the same job for
            the keyboard, above.
          */}
          <div
            aria-hidden="true"
            onClick={p.onToggleList}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute right-0 bottom-full left-0 z-20 border-t border-line bg-s1 shadow-e2">
            <div className="mx-auto max-w-3xl px-5 py-3">
              <h2 className="mb-2 text-[11px] font-medium tracking-[0.06em] text-muted uppercase">
                Unsaved changes
              </h2>
              <ul className="max-h-[40vh] divide-y divide-line/60 overflow-auto overscroll-contain">
                {p.changed.map((k) => (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => p.onGoTo(k)}
                      className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded px-1 py-2 text-left hover:bg-s2"
                    >
                      <span className="text-[13px] text-fg">{FIELD_DEFS[k].label}</span>
                      <span className="text-[11.5px] text-muted">
                        {TABS.find((t) => t.id === FIELD_DEFS[k].tab)?.label}
                      </span>
                      <span className="tabular ml-auto text-[12px] text-muted">
                        <span className="line-through opacity-70">
                          {displayValue(k, p.baseline[k])}
                        </span>
                        {' → '}
                        <span className="text-warn">{displayValue(k, p.draft[k])}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      ) : null}

      <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-2 px-5 py-3">
        <Button
          variant="primary"
          size="sm"
          disabled={!p.dirty || p.pending}
          loading={p.pending}
          onClick={p.onSave}
        >
          Update
        </Button>

        {p.dirty && !p.pending ? (
          <>
            {/*
              "3 unsaved changes" rather than "Unsaved changes". The button
              greys out until the form is dirty, which tells you when there is
              nothing to do but never the opposite — that a number typed two
              sections ago is still only in this browser.
            */}
            <button
              type="button"
              onClick={p.onToggleList}
              aria-expanded={listing}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[12.5px] text-warn hover:bg-warn-soft/40"
            >
              {p.changed.length} unsaved change{p.changed.length === 1 ? '' : 's'}
              <ChevronDown
                aria-hidden="true"
                className={cn('size-3.5 transition-transform duration-150', listing && 'rotate-180')}
              />
            </button>
            <Button variant="ghost" size="sm" onClick={p.onDiscard}>
              <Undo2 aria-hidden="true" />
              Discard
            </Button>
          </>
        ) : null}

        {p.saved ? (
          <span role="status" className="flex items-center gap-1 text-[12.5px] text-ok">
            <Check aria-hidden="true" className="size-3.5" />
            Saved
          </span>
        ) : null}

        {p.error ? (
          <span role="alert" className="text-[12.5px] text-danger">
            {errorMessage(p.error)}
          </span>
        ) : null}

        <span className="ml-auto text-[12px] text-muted">Applies to runs started from now.</span>
      </div>
    </div>
  )
}

function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message
    if (typeof m === 'string' && m.length > 0) return m
  }
  return 'Could not save.'
}
